import styled from '@emotion/styled'
import { ANCHOR, CUSTOM_AD_FORMAT, IN_CONTENT_PARALLAX, LEADERBOARD, SKIN_SINGLE_AD, SKIN_SIZE } from '@pubstack/common/src/adFormat'
import { AdUnit, VALIDATE_ADUNIT_SIZE_REGEX } from '@pubstack/common/src/adunit'
import { DeviceType } from '@pubstack/common/src/device-type'
import { NativeTemplate } from '@pubstack/common/src/nativeTemplate'
import { FunctionComponent, useMemo, useState } from 'react'
import { Controller, useFormContext, useWatch, Validate } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { BorderRadius } from '~/assets/style/tokens'
import Button from '~/components/Button'
import { CheckboxGroup } from '~/components/CheckboxGroup'
import Chip from '~/components/Chip'
import { ChipListChipRenderer, ChipListInput } from '~/components/ChipListInput'
import { Icon, IconName, icons } from '~/components/Icon'
import { BaseInputElement, Input } from '~/components/Input'
import { Link, LinkIcon, LinkLabel } from '~/components/Link'
import { normalizeAsTrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { RadioButtonGroup } from '~/components/RadioButtonGroup'
import { RadioGroup } from '~/components/RadioGroup'
import { Select } from '~/components/Select'
import { SelectorOptionCard } from '~/components/SelectorOptionCard'
import { Toggle, ToggleInput } from '~/components/Toggle'
import { Tooltip } from '~/components/Tooltip'
import { WidgetMessage } from '~/components/WidgetMessage'
import { WithClassName } from '~/types/utils'
import { areArraysEqual } from '~/utils/array'
import { AdFormatDisplay, useAdFormat } from './AdFormatDisplay'
import { AdUnitKeyValuesForm } from './AdUnitKeyValuesForm'
import { SizeRecommendationPopover } from './components/SizeRecommendationPopover'
import { AdUnitForm, ErrorMessage, getPredefinedSizes, isSizeFitsContainer } from './PureAdmAdUnitEditPage'
import { INSTREAM_DEFAULT_VALUES, INSTREAM_POSITION_OPTS, INSTREAM_PROTOCOLS_OPTS, INSTREAM_START_PLAYING_OPTS } from './PureAdmAdUnitFirstStep'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FormHeader = styled.div<{ isInstream?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
padding: ${({ isInstream }) => (isInstream ? '16px 0 0' : '16px 0')};
`

const FormWrapper = styled.div`
  max-width: 1000px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  ${Fonts.P1};
  h2 {
    ${Fonts.H2};
    font-weight: 500;
    gap: 4px;
    display: flex;
    align-items: center;
  }
`

const MediatypesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 34px;
`

const SizesChoice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 45px;
  margin-bottom: 35px;
  margin-top: 12px;
  width: 100%;

  > ${WidgetMessage} {
    margin-bottom: -25px;
    align-self: flex-start;
  }
`

const MediatypeHeader = styled.div`
  ${Fonts.P1};
  font-weight: 500;
`

const MediaTypeProperties = styled.div`
  ${Fonts.H2};
  font-weight: 500;
`

const SizesWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`

const SizeInput = styled.div`
  flex-grow: 1;
`

const MaxSizeFilter = styled.div`
  display: flex;
  gap: 8px;
`

const StandardSizes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
`

const SizeChip = styled(Chip)<{ isExcluded: boolean }>`
  ${({ isExcluded }) =>
    isExcluded &&
    `
    text-decoration: line-through;
  `}
`

export const Help = styled.div`
  ${Fonts.P2};
  ${Fonts.colors.SlateGrey};
`

export const FormToggle = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  > div:nth-of-type(1) {
    display: inline-flex;
    gap: 8px;
    align-items: center;
  }
  > div:nth-of-type(2) {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-left: 52px;
    flex-basis: 100%;
  }
`

const AdditionalBehaviour = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const AdditionalBehaviourInfo = styled.div`
  code {
    background-color: ${Colors.Platinum};
    padding: 0 4px;
    ${BorderRadius.style};
  }
  p {
    display: inline-flex;
    align-items: center;
    gap: 1ch;
  }
`

const AnchorExistingContainerRecommandation = styled.span`
  > ${Tooltip} {
    display: inline-flex;
    align-items: center;
  }
`

const TemplatesList = styled.div`
  display: flex;
  flex-direction: row;
  row-gap: 16px;
  column-gap: 16px;
  flex-wrap: wrap;
`

const SubtitleTemplateCard = styled.span`
  color: ${Colors.SlateGrey};
`

enum RestoreState {
  RESTORE = 'Restore',
  RESET = 'Reset',
}

type PureAdmAdUnitSecondStepProps = WithClassName & {
  adUnit: AdUnit
  nativeTemplates: NativeTemplate[]
  isLoading: boolean
  isEditing: boolean
}

const RecommendedSizesTooltip = styled(Tooltip)`
  display: inline-flex;
  align-items: center;
`
/*
const CollapsibleControl = styled.div`
  & > * {
    flex-direction: row-reverse;
  }
`*/

const InlineInputs = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;

  & > ${BaseInputElement} {
    width: 212px;
  }
`

const VerticalInputs = styled.div`
  width: 444px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const InstreamSettings = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const InstreamAdvancedSettings = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const OptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const TitleWrapper = styled.div`
  display: flex;
  flewx-direction: row;
  gap: 2px;
  height: 26px;
`
const TooltipIcon = styled(Icon)`
& > svg {
    width: 20px;
    height: 20px;
    padding-top: 2px;
    color: ${Colors.Hurricane};
  }
`
const RestoreSettingsButton = styled(Link)`
    flex-direction: row-reverse;
    width: fit-content;
    gap: 4px;
    ${LinkLabel} ~ ${LinkIcon} {
      margin-left: 0;
      margin-right: 4px;
    }
`

const getSizeChipRenderer: (isSizeExcluded: (size: string) => boolean, isSizeRecommended: (size: string) => boolean) => ChipListChipRenderer = (isSizeExcluded, isSizeRecommended) => {
  const SizeChipRenderer: ChipListChipRenderer = (chip, { value }) => {
    const isExcluded = isSizeExcluded(value)
    return <SizeChip {...chip} isExcluded={isExcluded} color={isSizeExcluded(value) ? Colors.Platinum : Colors.Sora} text={value} iconLeft={isSizeRecommended(value) ? { name: 'stars' } : undefined} />
  }
  return SizeChipRenderer
}

const UnstyledPureAdmAdUnitSecondStep: FunctionComponent<PureAdmAdUnitSecondStepProps> = ({ adUnit, nativeTemplates, isLoading, isEditing, className }) => {
  const {
    control,
    formState: { errors, dirtyFields },
    setValue,
    getValues,
    reset,
  } = useFormContext<AdUnitForm>()
  const { adFormat, sizes, customSizes, containerMaxSize, devices, bannerEnabled, skinEnabled, fluid, anchor, parallaxEnabled, dynamicEnabled, canOutstream, nativeTemplate } = useWatch({ control })

  const selectedAdFormat = useAdFormat(adFormat?.id)
  const skinFitsInContainer = isSizeFitsContainer(SKIN_SIZE, containerMaxSize).both

  const predefinedSizes = getPredefinedSizes(selectedAdFormat as AdFormatDisplay, devices as DeviceType[])
  const missingRecommendedSizes = predefinedSizes.filter((item) => (sizes || []).indexOf(item) < 0)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isRecommendedSizesPopoverOpen, setIsRecommendedSizesPopoverOpen] = useState(isEditing && missingRecommendedSizes.length > 0)
  const validateSizes: Validate<string[], AdUnit> = (sizesToValidate) => {
    if (!sizesToValidate.every((size) => VALIDATE_ADUNIT_SIZE_REGEX.test(size))) {
      return 'Sizes should follow this format : [Num]x[Num] (ex: 300x250)'
    }

    if (new Set(sizesToValidate).size !== sizesToValidate.length) {
      const duplicates = [...new Set(sizesToValidate.filter((size) => sizesToValidate.filter((s) => s === size).length > 1)).values()]
      return `Sizes can only be used once, please remove duplicates on: ${duplicates.join(', ')}`
    }

    if (containerMaxSize) {
      const isSizeValid = (size: string) => isSizeFitsContainer(size, containerMaxSize).both
      const unfittingSizes = sizesToValidate.map((size) => (isSizeValid(size) ? null : size)).filter((size) => size)
      if (unfittingSizes.length > 0) {
        if (unfittingSizes.length === sizesToValidate.length) {
          return `No sizes fit your criteria. Update your filters or click on "Apply size filter" to remove them.`
        }
        return `Some sizes do not fit your criteria. Click on "Apply size filter" to remove them.`
      }
    }

    if (selectedAdFormat?.id === SKIN_SINGLE_AD.id && sizesToValidate.length) {
      if (!skinFitsInContainer) {
        return `Skin size should not be filtered out for this ad format.`
      }
    }

    return true
  }

  const validateSkinSize: Validate<string[], AdUnit> = (sizes) => {
    if (selectedAdFormat?.id === SKIN_SINGLE_AD.id && !sizes.includes(SKIN_SIZE)) {
      return `Standard size '1800x1000' is mandatory for the Skin format`
    }

    return true
  }

  const getRestoreState = (sizesList: string[], currentSizes: string[]): RestoreState => {
    if (isEditing) {
      if (areArraysEqual(sizesList, currentSizes)) {
        return RestoreState.RESET
      } else {
        return currentSizes.length > 0 ? RestoreState.RESTORE : RestoreState.RESET
      }
    } else {
      return RestoreState.RESET
    }
  }

  const restoreSizes = (sizesList: string[], currentSizes: string[]): string[] => {
    const restoreState = getRestoreState(sizesList, currentSizes)
    switch (restoreState) {
      case RestoreState.RESTORE: {
        return currentSizes
      }
      case RestoreState.RESET: {
        return []
      }
    }
  }

  const dynamicToggleState = useMemo(() => {
    if (!selectedAdFormat?.isDynamicToggleEnabled) {
      return { disabled: true, message: 'Incompatible with the current ad format.' }
    }
    if (anchor?.mode) {
      return { disabled: true, message: 'Incompatible with anchor.' }
    }
    return { disabled: false }
  }, [selectedAdFormat, anchor])

  // adFormat was missing from the dependency list below, so the desktop check could go stale
  const anchorToggleState = (() => {
    if (selectedAdFormat?.id === ANCHOR.id) {
      return { disabled: true, message: 'Mandatory on this ad format.' }
    }
    if (!selectedAdFormat?.isAnchorToggleEnabled) {
      return { disabled: true, message: 'Incompatible with the current ad format.' }
    }
    if (parallaxEnabled) {
      return { disabled: true, message: 'Incompatible with parallax.' }
    }
    if (devices?.includes('desktop') && adFormat?.id !== CUSTOM_AD_FORMAT.id) {
      return { disabled: true, message: 'Incompatible with desktop.' }
    }
    if (dynamicEnabled) {
      return { disabled: true, message: 'Incompatible with dynamic.' }
    }
    return { disabled: false }
  })()

  const parallaxToggleState = useMemo(() => {
    if (selectedAdFormat?.id === IN_CONTENT_PARALLAX.id) {
      return { disabled: true, message: 'Mandatory on this ad format.' }
    }
    if (!selectedAdFormat?.isParallaxToggleEnabled) {
      return { disabled: true, message: 'Incompatible with the current ad format.' }
    }
    if (anchor?.mode) {
      return { disabled: true, message: 'Incompatible with anchor.' }
    }
    return { disabled: false }
  }, [selectedAdFormat, anchor])

  const convertToIconName = (iconName: string): IconName => {
    const allIconNames = Object.keys(icons) as IconName[]
    return allIconNames.find((key) => key === iconName) || 'native_large_rectangle'
  }

  return (
    <Wrapper className={className}>
      {selectedAdFormat?.mediatypes.includes('Instream') ? (
        <FormHeader isInstream={true}>
          <h2>Demand source</h2>
          Prebid only
        </FormHeader>
      ) : (
        <>
          <div>
            <FormHeader>
              <h2>Demand source</h2>
              Enable or disable header bidding for this ad unit.
            </FormHeader>
            <div>
              <FormToggle>
                <div>
                  <ToggleInput
                    disabled={isEditing && adUnit.headerBiddingEnabled}
                    id={'headerBiddingEnabled'}
                    control={control}
                    name={'headerBiddingEnabled'}
                    trackingLabel={adUnit.headerBiddingEnabled ? 'adm-ad-units-header-bidding-unset' : 'adm-ad-units-header-bidding-set'}
                  />
                  <span>Header bidding</span>
                </div>
              </FormToggle>
            </div>
          </div>
          <div>
            <FormHeader>
              <h2>Additional behaviour</h2>
            </FormHeader>
            <AdditionalBehaviour>
              <FormToggle>
                <div>
                  <ToggleInput
                    id={'dynamic'}
                    disabled={dynamicToggleState.disabled}
                    control={control}
                    name={'dynamicEnabled'}
                    trackingLabel={dynamicEnabled ? 'adm-ad-units-dynamic-unset' : 'adm-ad-units-dynamic-set'}
                  />
                  <span>Dynamic</span>
                </div>
                <div>
                  <Help>Allows the ad unit to be used multiple times on a single page. {dynamicToggleState.message}</Help>
                </div>
              </FormToggle>

              <FormToggle>
                <div>
                  <Toggle
                    disabled={anchorToggleState.disabled}
                    id={'anchor'}
                    value={!!anchor?.mode}
                    onClick={() => (!anchor?.mode ? setValue('anchor.mode', adUnit.anchor?.mode ?? 'createNewDiv', { shouldDirty: true }) : setValue('anchor.mode', undefined, { shouldDirty: true }))}
                    trackingLabel={anchor?.mode ? 'adm-ad-units-anchor-unset' : 'adm-ad-units-anchor-set'}
                  />
                  <span>Anchor</span>
                </div>
                <div>
                  <Help>Display the creative in a closable container at the bottom of the page. {anchorToggleState.message}</Help>
                  {errors.anchor && (
                    <ErrorMessage>
                      <Icon name={'alert'} width={'0.875rem'} />
                      {errors.anchor.message}
                    </ErrorMessage>
                  )}
                  {anchor?.mode && !errors.anchor && (
                    <RadioButtonGroup
                      name="anchor.mode"
                      control={control}
                      options={[
                        {
                          label: 'No container exists for this ad unit, create it automatically.',
                          value: 'createNewDiv',
                          trackingLabel: 'adm-ad-units-anchor-no-container-set',
                        },
                        {
                          label: (
                            <AnchorExistingContainerRecommandation>
                              Use existing container for this ad unit. It must be placed at the root of the page, as a direct child of &lt;body&gt;.{' '}
                              <Tooltip
                                title={
                                  <code>
                                    <pre>{'<body>\n\t...\n\t<div ID for this ad unit>\n</body>'}</pre>
                                  </code>
                                }
                              >
                                <Icon fill={Colors.Hurricane} name={'info'} width={'18px'} />
                              </Tooltip>
                            </AnchorExistingContainerRecommandation>
                          ),
                          value: 'useExistingDiv',
                          trackingLabel: 'adm-ad-units-anchor-existing-container-set',
                        },
                      ]}
                    />
                  )}
                </div>
              </FormToggle>

              <FormToggle>
                <div>
                  <ToggleInput
                    disabled={parallaxToggleState.disabled}
                    id={'parallax'}
                    control={control}
                    name={'parallaxEnabled'}
                    trackingLabel={parallaxEnabled ? 'adm-ad-units-parallax-unset' : 'adm-ad-units-parallax-set'}
                  />
                  <span>Parallax</span>
                </div>

                <div>
                  <Help>Display bigger creatives in a smaller container by keeping most of the creative viewable when scrolling. {parallaxToggleState.message}</Help>
                  {parallaxEnabled && !errors.parallaxEnabled && (
                    <WidgetMessage
                      icon={'info'}
                      message={
                        <AdditionalBehaviourInfo>
                          <p>
                            To ensure viewability, containers with a smaller height than <code>310px</code> will be resized to match that height.
                          </p>
                          <p>
                            If you want to avoid any CLS increase, only use parallax on ad units which container has a height of <code>310px</code> or greater.
                          </p>
                        </AdditionalBehaviourInfo>
                      }
                    />
                  )}
                </div>
              </FormToggle>
            </AdditionalBehaviour>
          </div>
        </>
      )}
      <div>
        <FormHeader>
          <h2>Media types</h2>
        </FormHeader>
        {selectedAdFormat?.mediatypes?.length && (
          <MediatypesWrapper>
            {selectedAdFormat.mediatypes.includes('Banner') && (
              <FormWrapper>
                <MediatypeHeader>Banner</MediatypeHeader>
                <FormToggle>
                  <div>
                    <ToggleInput
                      disabled={selectedAdFormat.mediatypes?.length === 1}
                      id={'bannerEnabled'}
                      control={control}
                      name={'bannerEnabled'}
                      trackingLabel={bannerEnabled ? 'adm-ad-units-banner-unset' : 'adm-ad-units-banner-set'}
                    />
                    <span>Banner</span>
                  </div>
                </FormToggle>
                {bannerEnabled && (
                  <>
                    {(selectedAdFormat.id === LEADERBOARD.id || selectedAdFormat.id === SKIN_SINGLE_AD.id) && (
                      <FormToggle>
                        <div>
                          <ToggleInput
                            id={'skinEnabled'}
                            control={control}
                            name={'skinEnabled'}
                            disabled={!skinFitsInContainer || selectedAdFormat.id === SKIN_SINGLE_AD.id}
                            trackingLabel={skinEnabled ? 'adm-ad-units-skin-unset' : 'adm-ad-units-skin-set'}
                          />
                          <span>Skin</span>
                        </div>
                      </FormToggle>
                    )}
                    <FormToggle>
                      <div>
                        <ToggleInput
                          id={'fluid'}
                          control={control}
                          rules={{
                            required: { value: (sizes ?? []).length === 0, message: 'Minimum one size or fluid required.' },
                          }}
                          name={'fluid'}
                          trackingLabel={fluid ? 'adm-ad-units-fluid-unset' : 'adm-ad-units-fluid-set'}
                        />
                        <span>Fluid (GAM)</span>
                        {errors.fluid && (
                          <ErrorMessage>
                            <Icon name={'alert'} width={'0.875rem'} />
                            {errors.fluid.message}
                          </ErrorMessage>
                        )}
                      </div>
                    </FormToggle>
                    <SizesChoice>
                      {adFormat?.id === ANCHOR.id && (
                        <WidgetMessage
                          icon={'info'}
                          message={
                            <AdditionalBehaviourInfo>
                              <p>
                                Anchor works best with creatives which height is of <code>100px</code> or less.
                                <Button size={'s'} onClick={() => setValue('containerMaxSize.height', 100)} variant="secondary" trackingLabel={'adm-ad-units-anchor-height-set'}>
                                  Set height filter
                                </Button>
                              </p>
                            </AdditionalBehaviourInfo>
                          }
                        />
                      )}
                      <MaxSizeFilter>
                        <Input
                          type={'number'}
                          label={'Max width filter'}
                          control={control}
                          name={'containerMaxSize.width'}
                          helper={'Optional'}
                          iconRight={'pixel'}
                          min={1}
                          rules={{
                            validate: {
                              value: (formValue) => {
                                return !formValue || Number(formValue) > 0 ? true : 'Should be greater than 0'
                              },
                            },
                          }}
                          error={errors.containerMaxSize?.width?.message}
                        />
                        <Input
                          type={'number'}
                          label={'Max height filter'}
                          control={control}
                          name={'containerMaxSize.height'}
                          helper={'Optional'}
                          iconRight={'pixel'}
                          min={1}
                          rules={{
                            validate: {
                              value: (formValue) => {
                                return !formValue || Number(formValue) > 0 ? true : 'Should be greater than 0'
                              },
                            },
                          }}
                          error={errors.containerMaxSize?.height?.message}
                        />
                        <Button
                          disabled={
                            (!containerMaxSize?.width && !containerMaxSize?.height) ||
                            [...(sizes ?? []), ...(customSizes ?? [])].filter((size) => !isSizeFitsContainer(size, containerMaxSize).both).length === 0
                          }
                          variant={'tertiary'}
                          onClick={() => {
                            setValue('sizes', sizes?.filter((size) => isSizeFitsContainer(size, containerMaxSize).both) ?? [])
                            setValue('customSizes', customSizes?.filter((size) => isSizeFitsContainer(size, containerMaxSize).both) ?? [])
                          }}
                          trackingLabel={'adm-ad-units-max-sizes-apply'}
                        >
                          Apply size filter
                        </Button>
                      </MaxSizeFilter>
                      <StandardSizes>
                        <SizesWrapper>
                          <SizeInput>
                            <ChipListInput
                              chipColor={Colors.Sora}
                              type={'text'}
                              label={'Standard sizes (px) - ad server, header bidding'}
                              control={control}
                              name={'sizes'}
                              helper={'Type a size (ex: 300x250) then press Enter or Space'}
                              error={errors.sizes?.message}
                              rules={{
                                validate: (sizes, adUnit) => {
                                  const validateResult = validateSizes(sizes, adUnit)
                                  return validateResult === true ? validateSkinSize(sizes, adUnit) : validateResult
                                },
                              }}
                              chipRenderer={getSizeChipRenderer(
                                (size) => !isSizeFitsContainer(size, containerMaxSize).both,
                                (size) => predefinedSizes.includes(size)
                              )}
                              iconRight={'pixel'}
                              additionalAction={
                                predefinedSizes.length
                                  ? {
                                      icon: 'stars',
                                      onClick: () => setIsRecommendedSizesPopoverOpen(!isRecommendedSizesPopoverOpen),
                                      trackingLabel: `adm-ad-units-size-recommandation-displaying-apply`,
                                      disabled: missingRecommendedSizes.length === 0,
                                      renderer: (action) => (
                                        <RecommendedSizesTooltip disabled={isRecommendedSizesPopoverOpen} title={missingRecommendedSizes.length ? 'Recommended sizes' : 'Recommended sizes applied'}>
                                          <SizeRecommendationPopover
                                            open={isRecommendedSizesPopoverOpen && !!missingRecommendedSizes.length}
                                            onClose={() => {
                                              setIsRecommendedSizesPopoverOpen(false)
                                            }}
                                            onAddAllSizes={(sizesToAdd) => setValue('sizes', [...(sizes ?? []), ...sizesToAdd])}
                                            onSizeClick={(size) => setValue('sizes', [...(sizes ?? []), size])}
                                            sizes={missingRecommendedSizes}
                                          >
                                            {action}
                                          </SizeRecommendationPopover>
                                        </RecommendedSizesTooltip>
                                      ),
                                    }
                                  : undefined
                              }
                            />
                          </SizeInput>

                          <Tooltip title={getRestoreState(sizes as string[], adUnit.sizes)} disabled={areArraysEqual(sizes as string[], adUnit.sizes)}>
                            <Button
                              variant={'tertiary'}
                              iconName={'restore'}
                              iconSize={'26px'}
                              disabled={areArraysEqual(sizes as string[], adUnit.sizes)}
                              onClick={() => setValue('sizes', restoreSizes(sizes as string[], adUnit.sizes))}
                              trackingLabel={'adm-ad-units-sizes-restore'}
                            />
                          </Tooltip>
                        </SizesWrapper>
                      </StandardSizes>
                      <SizesWrapper>
                        <SizeInput>
                          <ChipListInput
                            chipColor={Colors.Turquoise}
                            isColorVariable
                            type={'text'}
                            label={'Custom sizes (px) - ad server only'}
                            control={control}
                            name={'customSizes'}
                            helper={'Optional. Type a size (ex: 300x250) then press Enter or Space'}
                            error={errors.customSizes?.message}
                            rules={{
                              validate: validateSizes,
                            }}
                            chipRenderer={getSizeChipRenderer(
                              (size) => !isSizeFitsContainer(size, containerMaxSize).both,
                              (size) => predefinedSizes.includes(size)
                            )}
                            iconRight={'pixel'}
                          />
                        </SizeInput>
                        <Tooltip title={getRestoreState(customSizes as string[], adUnit.customSizes)} disabled={customSizes?.length === 0}>
                          <Button
                            variant={'tertiary'}
                            iconName={'restore'}
                            iconSize={'26px'}
                            disabled={customSizes?.length === 0}
                            onClick={() => setValue('customSizes', restoreSizes(customSizes as string[], adUnit.customSizes))}
                            trackingLabel={'adm-ad-units-custom-sizes-restore'}
                          />
                        </Tooltip>
                      </SizesWrapper>
                    </SizesChoice>
                  </>
                )}
              </FormWrapper>
            )}
            {selectedAdFormat?.mediatypes.includes('Outstream') && (
              <FormWrapper>
                <MediatypeHeader>Outstream</MediatypeHeader>
                <FormToggle>
                  <div>
                    <ToggleInput
                      id={'canOutstream'}
                      control={control}
                      name={'canOutstream'}
                      disabled={selectedAdFormat.mediatypes?.length === 1}
                      trackingLabel={canOutstream ? 'adm-ad-units-outstream-unset' : 'adm-ad-units-outstream-set'}
                    />
                    <span>Outstream</span>
                  </div>
                </FormToggle>
              </FormWrapper>
            )}
            {selectedAdFormat?.mediatypes.includes('Instream') && (
              <FormWrapper>
                <MediatypeHeader>Instream</MediatypeHeader>
                <SizesChoice>
                  <StandardSizes>
                    <SizesWrapper>
                      <SizeInput>
                        <ChipListInput
                          chipColor={Colors.Sora}
                          type={'text'}
                          label={'Player size'}
                          control={control}
                          name={'sizes'}
                          helper={'Type a size (ex: 300x250) then press Enter or Space'}
                          error={errors.sizes?.message}
                          rules={{
                            validate: (sizes, adUnit) => {
                              return !sizes || sizes.length === 0 ? 'Minimum one size required.' : validateSizes(sizes, adUnit)
                            },
                          }}
                          chipRenderer={getSizeChipRenderer(
                            (size) => !isSizeFitsContainer(size, containerMaxSize).both,
                            (size) => predefinedSizes.includes(size)
                          )}
                          iconRight={'pixel'}
                          additionalAction={
                            predefinedSizes.length
                              ? {
                                  icon: 'stars',
                                  onClick: () => setIsRecommendedSizesPopoverOpen(!isRecommendedSizesPopoverOpen),
                                  trackingLabel: `adm-ad-units-size-recommandation-displaying-apply`,
                                  disabled: missingRecommendedSizes.length === 0,
                                  renderer: (action) => (
                                    <RecommendedSizesTooltip disabled={isRecommendedSizesPopoverOpen} title={missingRecommendedSizes.length ? 'Recommended sizes' : 'Recommended sizes applied'}>
                                      <SizeRecommendationPopover
                                        open={isRecommendedSizesPopoverOpen && !!missingRecommendedSizes.length}
                                        onClose={() => {
                                          setIsRecommendedSizesPopoverOpen(false)
                                        }}
                                        onAddAllSizes={(sizesToAdd) => setValue('sizes', [...(sizes ?? []), ...sizesToAdd])}
                                        onSizeClick={(size) => setValue('sizes', [...(sizes ?? []), size])}
                                        sizes={missingRecommendedSizes}
                                      >
                                        {action}
                                      </SizeRecommendationPopover>
                                    </RecommendedSizesTooltip>
                                  ),
                                }
                              : undefined
                          }
                        />
                      </SizeInput>

                      <Tooltip title={getRestoreState(sizes as string[], adUnit.sizes)} disabled={areArraysEqual(sizes as string[], adUnit.sizes)}>
                        <Button
                          variant={'tertiary'}
                          iconName={'restore'}
                          iconSize={'26px'}
                          disabled={areArraysEqual(sizes as string[], adUnit.sizes)}
                          onClick={() => setValue('sizes', restoreSizes(sizes as string[], adUnit.sizes))}
                          trackingLabel={'adm-ad-units-sizes-restore'}
                        />
                      </Tooltip>
                    </SizesWrapper>
                  </StandardSizes>
                </SizesChoice>
                <InstreamSettings>
                  <h2>Settings</h2>
                  <InlineInputs>
                    <Input
                      name={'instreamParams.mediaTypeParams.minduration'}
                      label={'Minimal ad duration'}
                      textRight={'s'}
                      type={'number'}
                      min={1}
                      max={getValues('instreamParams.mediaTypeParams.maxduration') ?? 1}
                      error={(getValues('instreamParams.mediaTypeParams.minduration') ?? 0) >= 1 ? false : 'Must be at least 1'}
                    />
                    <Input
                      name={'instreamParams.mediaTypeParams.maxduration'}
                      label={'Maximal ad duration'}
                      textRight={'s'}
                      type={'number'}
                      min={getValues('instreamParams.mediaTypeParams.minduration') ?? 1}
                      error={
                        (getValues('instreamParams.mediaTypeParams.maxduration') ?? 0) >= (getValues('instreamParams.mediaTypeParams.minduration') ?? 0) ? false : 'Must be greater than minimal value'
                      }
                    />
                  </InlineInputs>
                  <>
                    <Link
                      label={'Advanced settings'}
                      icon={isCollapsed ? 'chevron_up' : 'chevron_down'}
                      trackingLabel={'adm-ad-units-instream-advanced-settings-set'}
                      onClick={() => setIsCollapsed(!isCollapsed)}
                    />
                    {isCollapsed && (
                      <InstreamAdvancedSettings>
                        <VerticalInputs>
                          <h2>Playing settings</h2>
                          <Controller
                            name="instreamParams.playbackmethod"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label={'Start playing when'}
                                options={INSTREAM_START_PLAYING_OPTS}
                                onChange={(option) => {
                                  field.onChange(option.value)
                                }}
                              />
                            )}
                          />
                          <Controller
                            name="instreamParams.mediaTypeParams.pos"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label={'Position on screen (pos)'}
                                options={INSTREAM_POSITION_OPTS}
                                onChange={(option) => {
                                  field.onChange(option.value)
                                }}
                              />
                            )}
                          />
                        </VerticalInputs>
                        <OptionWrapper>
                          <p>Allow skippable ads</p>
                          <div>
                            <RadioGroup
                              control={control}
                              name={'instreamParams.skippableads'}
                              variant={'secondary'}
                              options={[
                                { label: 'Yes', value: true, trackingLabel: 'adm-ad-units-instream-skippable-set' },
                                { label: 'No', value: false, trackingLabel: 'adm-ad-units-instream-not-skippable-set' },
                              ]}
                            />
                          </div>
                        </OptionWrapper>
                        <OptionWrapper>
                          <TitleWrapper>
                            Placement (plcmt)
                            <Tooltip
                              title={
                                <>
                                  <b>Instream:</b> when the video is the main content of a page.
                                  <br />
                                  <b>Accompanying content:</b> when video is not the main content.
                                </>
                              }
                            >
                              <TooltipIcon name="info"></TooltipIcon>
                            </Tooltip>
                          </TitleWrapper>
                          <div>
                            <RadioGroup
                              control={control}
                              name={'instreamParams.mediaTypeParams.plcmt'}
                              variant={'secondary'}
                              options={[
                                { label: 'Instream', value: 1, trackingLabel: 'adm-ad-units-instream-position-set' },
                                { label: 'Accompanying content', value: 2, trackingLabel: 'adm-ad-units-instream-position-accompanying-set' },
                              ]}
                            />
                          </div>
                        </OptionWrapper>
                        <h2>Video settings</h2>
                        <InlineInputs>
                          <Input
                            name={'instreamParams.mediaTypeParams.minbitrate'}
                            label={'Minimal bit rate'}
                            textRight={'kbps'}
                            type={'number'}
                            min={1}
                            max={getValues('instreamParams.mediaTypeParams.maxbitrate') ?? 1}
                            error={(getValues('instreamParams.mediaTypeParams.minbitrate') ?? 0) >= 1 ? false : 'Must be at least 1'}
                          />
                          <Input
                            name={'instreamParams.mediaTypeParams.maxbitrate'}
                            label={'Maximal bit rate'}
                            textRight={'kbps'}
                            type={'number'}
                            min={getValues('instreamParams.mediaTypeParams.minbitrate') ?? 1}
                            error={
                              (getValues('instreamParams.mediaTypeParams.maxbitrate') ?? 0) >= (getValues('instreamParams.mediaTypeParams.minbitrate') ?? 0)
                                ? false
                                : 'Must be greater than minimal value'
                            }
                          />
                        </InlineInputs>
                        <OptionWrapper>
                          <p>Video protocols allowed:</p>
                          <CheckboxGroup name="instreamParams.protocols" control={control} options={INSTREAM_PROTOCOLS_OPTS} vertical={true} />
                        </OptionWrapper>
                        {Object.keys(dirtyFields).length > 0 && (
                          <RestoreSettingsButton
                            icon={'refresh'}
                            iconSize="23px"
                            label={'Restore default settings'}
                            onClick={() => reset({ ...getValues(), instreamParams: INSTREAM_DEFAULT_VALUES })}
                            trackingLabel="adm-ad-units-instream-restore-settings-set"
                          ></RestoreSettingsButton>
                        )}
                      </InstreamAdvancedSettings>
                    )}
                  </>
                </InstreamSettings>
              </FormWrapper>
            )}
            {selectedAdFormat?.mediatypes.includes('Native') && (
              <FormWrapper>
                <MediatypeHeader>Native</MediatypeHeader>
                <FormToggle>
                  <div>
                    <Toggle
                      value={!!nativeTemplate}
                      onClick={() => {
                        setValue('nativeTemplate', nativeTemplate ? undefined : nativeTemplates?.[0].id, { shouldDirty: true })
                      }}
                      disabled={selectedAdFormat.mediatypes?.length === 1}
                      trackingLabel={nativeTemplate ? 'adm-ad-units-native-unset' : 'adm-ad-units-native-set'}
                    />
                    <span>Native (Prebid)</span>
                  </div>
                </FormToggle>
                {!!nativeTemplate && (
                  <>
                    <MediaTypeProperties>Templates</MediaTypeProperties>
                    <TemplatesList>
                      {nativeTemplates.map((template) => (
                        <SelectorOptionCard
                          key={template.id}
                          iconName={convertToIconName(template.iconName)}
                          title={template.name}
                          subtitle={<SubtitleTemplateCard>{template.description}</SubtitleTemplateCard>}
                          isSelected={nativeTemplate === template.id}
                          onClick={() => {
                            setValue('nativeTemplate', template.id, { shouldDirty: true })
                          }}
                          trackingLabel={`adm-ad-units-${normalizeAsTrackingLabel(template.name)}-template-set`}
                        />
                      ))}
                    </TemplatesList>
                  </>
                )}
              </FormWrapper>
            )}
          </MediatypesWrapper>
        )}
      </div>
      {!isLoading && (
        <FormWrapper>
          <FormHeader>
            <h2>Key-values (optional)</h2>
          </FormHeader>
          <AdUnitKeyValuesForm
            updateAdUnit={(keyValues) => {
              setValue('slotKeyValues', keyValues, { shouldDirty: true })
            }}
            keyValues={(adUnit.slotKeyValues as AdUnit['slotKeyValues']) ?? []}
          />
        </FormWrapper>
      )}
    </Wrapper>
  )
}

export const PureAdmAdUnitSecondStep = styled(UnstyledPureAdmAdUnitSecondStep)``
